/**
 * MetadataTableColumnCustomizer Component
 *
 * Modal for customizing visible columns and their order.
 * Features preset views (Essential, Standard, Complete) and drag-reorder.
 *
 * Also includes a GLOBAL toggle for the Actions column visibility.
 * The Actions toggle applies to ALL MetadataTable instances, not just this table.
 */

import { useState, useCallback, useMemo, useEffect } from 'react'

// Preset counts - computed from metadata flags
interface PresetCounts {
  essential: number
  standard: number
  complete: number
}
import { Modal, Checkbox, Button, Space, Typography, Divider, Tooltip } from 'antd'
import { ReloadOutlined, ThunderboltOutlined, InfoCircleOutlined } from '@ant-design/icons'
import type { MetadataTableColumnCustomizerProps, PresetView } from './types'

const { Text } = Typography

/**
 * Preset button component
 */
function PresetButton({
  label,
  preset,
  currentPreset,
  onClick,
}: {
  label: string
  preset: PresetView
  currentPreset: PresetView
  onClick: () => void
}) {
  return (
    <Button
      type={currentPreset === preset ? 'primary' : 'default'}
      onClick={onClick}
      size="small"
    >
      {label}
    </Button>
  )
}

/**
 * MetadataTableColumnCustomizer - Column visibility and order customization
 */
export function MetadataTableColumnCustomizer({
  availableColumns,
  visibleColumns,
  preset,
  onColumnsChange,
  open,
  onClose,
  actionsVisible,
  onActionsVisibleChange,
}: MetadataTableColumnCustomizerProps) {
  // Local state for editing (commit on OK)
  const [localVisible, setLocalVisible] = useState<string[]>(visibleColumns)
  const [localPreset, setLocalPreset] = useState<PresetView>(preset)

  // Local state for Actions column toggle (commit on OK, discard on Cancel)
  const [localActionsVisible, setLocalActionsVisible] = useState<boolean>(actionsVisible ?? true)

  // Reset local state when modal opens
  useEffect(() => {
    if (open) {
      setLocalVisible(visibleColumns)
      setLocalPreset(preset)
      setLocalActionsVisible(actionsVisible ?? true)
    }
  }, [open, visibleColumns, preset, actionsVisible])

  // Compute preset counts from metadata flags
  const presetCounts = useMemo<PresetCounts>(() => {
    const essential = availableColumns.filter((c) => c.tableDefault === true).length
    const standard = availableColumns.filter((c) => c.recommended === true).length
    const complete = availableColumns.length
    return { essential, standard, complete }
  }, [availableColumns])

  // Group columns by section
  const columnsBySection = useMemo(() => {
    const groups: Map<string, typeof availableColumns> = new Map()

    availableColumns.forEach((col) => {
      const section = col.formSection ?? ''
      if (!groups.has(section)) {
        groups.set(section, [])
      }
      groups.get(section)!.push(col)
    })

    return Array.from(groups.entries())
      .filter(([, cols]) => cols.length > 0)
      .map(([section, cols]) => ({
        section: section || 'Other',
        columns: cols,
      }))
      // Sort so "Other" appears at the bottom
      .sort((a, b) => {
        if (a.section === 'Other') return 1
        if (b.section === 'Other') return -1
        return 0
      })
  }, [availableColumns])

  // Toggle column visibility
  // When adding a column, insert it at the correct position to maintain form-based order
  // (availableColumns is already sorted by form_section.order then form_order)
  const toggleColumn = useCallback((key: string) => {
    setLocalVisible((prev) => {
      const isVisible = prev.includes(key)
      if (isVisible) {
        return prev.filter((k) => k !== key)
      } else {
        // Find the correct insertion position to maintain form-based order
        const availableKeys = availableColumns.map((c) => c.key)
        const targetIndex = availableKeys.indexOf(key)

        // Find the first column in prev that should come AFTER key in the canonical order
        let insertIndex = prev.length
        for (let i = 0; i < prev.length; i++) {
          const currentIndex = availableKeys.indexOf(prev[i])
          if (currentIndex > targetIndex) {
            insertIndex = i
            break
          }
        }

        const result = [...prev]
        result.splice(insertIndex, 0, key)
        return result
      }
    })
    setLocalPreset('custom')
  }, [availableColumns])

  // Apply preset - uses metadata flags instead of position-based slicing
  const applyPreset = useCallback((newPreset: PresetView) => {
    let newVisible: string[]

    switch (newPreset) {
      case 'essential':
        // Essential = fields marked with table_default: true in YAML
        newVisible = availableColumns
          .filter((c) => c.tableDefault === true)
          .map((c) => c.key)
        break
      case 'standard':
        // Standard = fields marked with recommended: true in YAML
        newVisible = availableColumns
          .filter((c) => c.recommended === true)
          .map((c) => c.key)
        break
      case 'complete':
        // Complete = all visible columns
        newVisible = availableColumns.map((c) => c.key)
        break
      default:
        return
    }

    setLocalVisible(newVisible)
    setLocalPreset(newPreset)
  }, [availableColumns])

  // Reset to defaults (Essential)
  const handleReset = useCallback(() => {
    applyPreset('essential')
  }, [applyPreset])

  // Save changes
  const handleOk = useCallback(() => {
    onColumnsChange(localVisible, localPreset)
    // Only call onActionsVisibleChange if provided and value changed
    if (onActionsVisibleChange && localActionsVisible !== actionsVisible) {
      onActionsVisibleChange(localActionsVisible)
    }
    onClose()
  }, [localVisible, localPreset, onColumnsChange, onClose, onActionsVisibleChange, localActionsVisible, actionsVisible])

  // Cancel changes
  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  // Check if column is visible
  const isVisible = useCallback(
    (key: string) => localVisible.includes(key),
    [localVisible]
  )

  return (
    <Modal
      title="Customize Columns"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      width={500}
      okText="Apply"
      cancelText="Cancel"
    >
      {/* Preset buttons */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>Quick Presets:</Text>
        <div style={{ marginTop: 8 }}>
          <Space>
            <PresetButton
              label={`Essential (${presetCounts.essential})`}
              preset="essential"
              currentPreset={localPreset}
              onClick={() => applyPreset('essential')}
            />
            <PresetButton
              label={`Standard (${presetCounts.standard})`}
              preset="standard"
              currentPreset={localPreset}
              onClick={() => applyPreset('standard')}
            />
            <PresetButton
              label={`Complete (${presetCounts.complete})`}
              preset="complete"
              currentPreset={localPreset}
              onClick={() => applyPreset('complete')}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Column checkboxes grouped by section */}
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {columnsBySection.map(({ section, columns }) => (
          <div key={section} style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {section}
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {columns.map((col) => (
                <Checkbox
                  key={col.key}
                  checked={isVisible(col.key)}
                  onChange={() => toggleColumn(col.key)}
                  style={{ marginLeft: 0, width: 'calc(50% - 4px)' }}
                >
                  {col.title}
                </Checkbox>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Summary */}
      <Text type="secondary">
        {localVisible.length} of {availableColumns.length} columns selected
      </Text>

      {/* Actions Column Toggle - simple checkbox with info tooltip */}
      {onActionsVisibleChange && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--rp-border-subtle)' }}>
          <Checkbox
            checked={localActionsVisible}
            onChange={(e) => setLocalActionsVisible(e.target.checked)}
          >
            <Space>
              <ThunderboltOutlined />
              <span>Show Actions Column</span>
              <Tooltip title="Applies to all tables">
                <InfoCircleOutlined style={{ color: 'var(--rp-gray-500)', fontSize: 12 }} />
              </Tooltip>
            </Space>
          </Checkbox>
        </div>
      )}
    </Modal>
  )
}

export default MetadataTableColumnCustomizer
